import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  input,
  NgZone,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { debounceTime, tap } from 'rxjs';
import { MarkdownViewerComponent } from '../markdown-viewer';
import { TocItem } from './table-of-contents.model';

/** Pixel offset from left edge for each heading depth (clerk variant). */
function getItemOffset(depth: number): number {
  if (depth <= 1) {
    return 14;
  }
  if (depth === 2) {
    return 26;
  }
  if (depth === 3) {
    return 38;
  }
  return 48;
}

/** Left-side line offset for each heading depth (clerk variant). */
function getLineOffset(depth: number): number {
  if (depth <= 1) {
    return 0;
  }
  if (depth === 2) {
    return 10;
  }
  if (depth === 3) {
    return 20;
  }
  return 28;
}

/** Width of the SVG stroke for the depth-aware line (also determines thumb width). */
const LINE_STROKE_WIDTH = 1;

@Component({
  selector: 'dojo-table-of-contents',
  templateUrl: './table-of-contents.component.html',
  styleUrl: './table-of-contents.component.scss',
  host: { class: 'block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MarkdownViewerComponent],
})
export class TableOfContentsComponent implements AfterViewInit {
  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly zone: NgZone,
  ) {
    // Recalculate SVG path and thumb when items change
    toObservable(this.items)
      .pipe(
        debounceTime(0),
        tap(() => {
          requestAnimationFrame(() => {
            this.updateSvgPath();
            this.updateThumbPosition();
          });
        }),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  /** TOC items with active state pre-computed by the consumer. */
  readonly items = input.required<TocItem[]>();

  /** Emitted when a TOC item is clicked. Consumer handles navigation. */
  readonly itemClick = output<string>();

  private readonly itemsContainerRef = viewChild<ElementRef<HTMLElement>>('itemsContainer');

  /** SVG path data for the clerk depth-aware line. */
  readonly svgData = signal<{ path: string; width: number; height: number } | null>(null);

  /** Precomputed mask-image CSS value for the SVG line. */
  readonly svgMaskImage = computed(() => {
    const svg = this.svgData();
    if (!svg) {
      return null;
    }
    const svgMarkup = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${svg.width} ${svg.height}"><path d="${svg.path}" stroke="black" stroke-width="${LINE_STROKE_WIDTH}" fill="none" /></svg>`;
    return `url("data:image/svg+xml,${encodeURIComponent(svgMarkup)}")`;
  });

  /** Exposed for template binding. */
  readonly lineStrokeWidth = LINE_STROKE_WIDTH;

  /** Thumb position (top offset and height in pixels). */
  readonly thumbTop = signal(0);
  readonly thumbHeight = signal(0);

  private resizeObserver: ResizeObserver | null = null;

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  ngAfterViewInit(): void {
    this.setupResizeObserver();

    this.destroyRef.onDestroy(() => {
      this.resizeObserver?.disconnect();
    });
  }

  // ---------------------------------------------------------------------------
  // Public methods (template-bound)
  // ---------------------------------------------------------------------------

  getItemOffset(depth: number): number {
    return getItemOffset(depth);
  }

  onItemClick(id: string): void {
    this.itemClick.emit(id);
  }

  // ---------------------------------------------------------------------------
  // ResizeObserver
  // ---------------------------------------------------------------------------

  private setupResizeObserver(): void {
    const container = this.itemsContainerRef()?.nativeElement;
    if (!container) {
      return;
    }

    this.zone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateSvgPath();
        this.updateThumbPosition();
      });
      this.resizeObserver.observe(container);
    });
  }

  // ---------------------------------------------------------------------------
  // SVG path (clerk depth-aware line)
  // ---------------------------------------------------------------------------

  private updateSvgPath(): void {
    const container = this.itemsContainerRef()?.nativeElement;
    if (!container || container.clientHeight === 0) {
      return;
    }

    const items = this.items();
    if (items.length === 0) {
      return;
    }

    let w = 0;
    let h = 0;
    const d: string[] = [];

    for (let i = 0; i < items.length; i++) {
      const element: HTMLElement | null = container.querySelector(`a[data-toc-item-id="${items[i].id}"]`);
      if (!element) {
        continue;
      }

      const styles = getComputedStyle(element);
      const offset = getLineOffset(items[i].level) + 1;
      const top = element.offsetTop + parseFloat(styles.paddingTop);
      const bottom = element.offsetTop + element.clientHeight - parseFloat(styles.paddingBottom);

      w = Math.max(offset, w);
      h = Math.max(h, bottom);

      d.push(`${i === 0 ? 'M' : 'L'}${offset} ${top}`);
      d.push(`L${offset} ${bottom}`);
    }

    this.zone.run(() =>
      this.svgData.set({
        path: d.join(' '),
        width: w + LINE_STROKE_WIDTH,
        height: h,
      }),
    );
  }

  // ---------------------------------------------------------------------------
  // Thumb position (spans from first to last active item)
  // ---------------------------------------------------------------------------

  private updateThumbPosition(): void {
    const container = this.itemsContainerRef()?.nativeElement;
    const items = this.items();
    if (!container || container.clientHeight === 0) {
      this.zone.run(() => {
        this.thumbTop.set(0);
        this.thumbHeight.set(0);
      });
      return;
    }

    let upper = Number.MAX_VALUE;
    let lower = 0;

    for (const item of items) {
      if (!item.isActive) {
        continue;
      }

      const element: HTMLElement | null = container.querySelector(`a[data-toc-item-id="${item.id}"]`);
      if (!element) {
        continue;
      }

      const styles = getComputedStyle(element);
      const top = element.offsetTop + parseFloat(styles.paddingTop);
      const bottom = element.offsetTop + element.clientHeight - parseFloat(styles.paddingBottom);

      upper = Math.min(upper, top);
      lower = Math.max(lower, bottom);
    }

    if (upper === Number.MAX_VALUE) {
      this.zone.run(() => {
        this.thumbTop.set(0);
        this.thumbHeight.set(0);
      });
      return;
    }

    this.zone.run(() => {
      this.thumbTop.set(upper);
      this.thumbHeight.set(lower - upper);
    });
  }
}
